import apiClient from "./apiClient";
import { shareInFlight } from "./inFlight";

// ---------------------------------------------------------------------------
// Orient Motor API wrappers. Master lookups feed the funnel dropdowns; the
// pipeline calls drive quote → covers → additional-details → issuance.
// All routes are mounted under /motor on the backend.
// ---------------------------------------------------------------------------

// --- Master / reference lookups --------------------------------------------

// --- Local case persistence ------------------------------------------------

export const syncMotorData = async (caseId, body) => {
  const { data } = await apiClient.post("/motor/sync", { case_id: caseId, ...body });
  return data;
};

export const fetchMotorData = async (caseId) => {
  const { data } = await apiClient.get(`/motor/${caseId}`);
  return data;
};

// --- Orient policy pipeline ------------------------------------------------

// Quote step: creates (or reuses) the quotation and returns the available
// plans/covers with premiums.
//
// This one MINTS a quotation at Orient, so it must not run twice for the same
// case — two quotations for one case is what that produced.
export const createPolicyHeader = (caseId, body = {}) =>
  shareInFlight(`policy-header:${caseId}`, () =>
    apiClient.post(`/motor/${caseId}/policy-header`, body).then(({ data }) => data)
  );

// Chosen a plan from an insurer that quotes but does not issue online (see
// services/carriers/contract.js `indicativeOnly`). The server re-rates the
// case, records the plan and its premium, and flags the case for an agent —
// nothing here continues into the issuance pipeline.
export const requestCallback = async (caseId, { carrierId, planCode }) => {
  const { data } = await apiClient.post(`/motor/${caseId}/callback-request`, {
    carrierId,
    planCode,
  });
  return data;
};

export const savePolicyWithCover = async (caseId, body) => {
  const { data } = await apiClient.post(`/motor/${caseId}/covers`, body);
  return data;
};

export const saveAdditionalDetails = async (caseId, body = {}) => {
  const { data } = await apiClient.post(`/motor/${caseId}/additional-details`, body);
  return data;
};

// Hands a quotation that Orient flagged for underwriting to an underwriter.
// approveQuotation submits this itself when Orient answers with error code 21,
// so this is the manual path — re-submitting after a failed auto-submit.
export const submitReferral = async (caseId, body = {}) => {
  const { data } = await apiClient.post(`/motor/${caseId}/referral`, body);
  return data;
};

export const approveQuotation = async (caseId, body = {}) => {
  const { data } = await apiClient.post(`/motor/${caseId}/approve`, body);
  return data;
};

// The declaration wording comes from the server so the dialog and the generated
// proposal form cannot drift apart.
export const fetchProposalDeclaration = async () => {
  const { data } = await apiClient.get("/motor/proposal-declaration");
  return data?.declaration || [];
};

export const retrieveQuoteSummary = async (caseId) => {
  const { data } = await apiClient.get(`/motor/${caseId}/summary`);
  return data;
};
